import {
    type FC
} from "react";
import {
    View
} from "react-native";
import type {
    DataTableStateProps
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useDataTableContext
} from "../../context";
import {
    Inbox as InboxIcon,
    Lock as LockIcon
} from "lucide-react-native";
import Button from "../../../button";
import Text from "../../../text";

const DataTableState: FC<DataTableStateProps> = ({
    customTheme: customThemeProp,
    kind,
    style
}) => {
    const context = useDataTableContext({
        part: "Content"
    });

    const {
        controlHeights,
        shapeConfigs,
        iconSizes,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context?.customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    const {
        container: containerDynamicStyle,
        content: contentDynamicStyle,
        icon: iconDynamicStyle
    } = useStyles({
        controlHeights,
        shapeConfigs,
        radiuses,
        colors,
        spaces
    });

    if(!context) {
        return null;
    }

    const currentTheme = customThemeProp ?? context.customTheme;

    const isSearched = !!context.globalFilter.trim();

    const isFiltered = isSearched || Object.keys(context.columnFilters).length > 0;

    const StateIcon = kind === "permission-denied" ? LockIcon : InboxIcon;

    const getTitle = () => {
        if(kind === "permission-denied") {
            return localize("no-view-permission");
        }

        return localize(isFiltered ? "no-results" : "data-table-empty-title");
    };

    const getDescription = () => {
        if(kind === "permission-denied") {
            return undefined;
        }

        if(!isFiltered) {
            return localize("data-table-empty-description");
        }

        return isSearched ? localize("no-results-for-search", [
            context.globalFilter
        ]) : localize("data-table-no-results-filters");
    };

    const description = getDescription();

    return <View
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        role="status"
    >
        {
            context.renderEmpty && kind === "empty" ?
                context.renderEmpty()
            :
                <View
                    style={[
                        stylesheet.content,
                        contentDynamicStyle
                    ]}
                >
                    <View
                        style={[
                            stylesheet.icon,
                            iconDynamicStyle
                        ]}
                        aria-hidden={true}
                    >
                        <StateIcon
                            color={colors.content.icon.mid}
                            size={iconSizes.large}
                        />
                    </View>
                    <Text
                        customTheme={currentTheme}
                        variant="titleSmallSize"
                        style={stylesheet.text}
                        color="high"
                    >
                        {getTitle()}
                    </Text>
                    {
                        description ?
                            <Text
                                customTheme={currentTheme}
                                variant="bodyMediumSize"
                                style={stylesheet.text}
                                color="mid"
                            >
                                {description}
                            </Text>
                        :
                            null
                    }
                    {
                        isFiltered && kind === "empty" ?
                            <Button
                                onPress={() => context.handleClearFilters(true)}
                                title={localize("data-table-clear-filters")}
                                customTheme={currentTheme}
                                variant="outline"
                                type="neutral"
                                size="small"
                            />
                        :
                            null
                    }
                </View>
        }
    </View>;
};
export default DataTableState;
